import React, { useRef, useState } from 'react';
import { extractNew, extractAdd } from '../../api/pdf';
import { useCompanies } from '../../hooks/useCompanies';
import { ExtractionResult, Company } from '../../types';

interface PdfUploadProps {
  mode?: 'new' | 'add';
  companyId?: string;
  onSuccess?: (result: ExtractionResult | { profile: unknown; document_id: string; confidence: unknown }) => void;
  onCancel?: () => void;
}

export function PdfUpload({ mode = 'new', companyId, onSuccess, onCancel }: PdfUploadProps) {
  const [selectedMode, setSelectedMode] = useState<'new' | 'add'>(companyId ? 'add' : mode);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>(companyId ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ExtractionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: companiesData } = useCompanies({ limit: 100, is_buyer: true });
  const companies: Company[] = companiesData?.data ?? [];

  const handleFile = (f: File) => {
    if (f.type !== 'application/pdf') {
      setError('Nur PDF-Dateien sind erlaubt.');
      return;
    }
    setFile(f);
    setError(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleSubmit = async () => {
    if (!file) return;
    if (selectedMode === 'add' && !selectedCompanyId) {
      setError('Bitte ein Unternehmen auswählen.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (selectedMode === 'new') {
        const res = await extractNew(file);
        setResult(res.data as ExtractionResult);
        onSuccess?.(res.data);
      } else {
        const res = await extractAdd(selectedCompanyId, file);
        onSuccess?.(res.data);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Upload fehlgeschlagen';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div>
        <div style={{
          background: 'var(--success-bg)',
          border: '1px solid var(--success)',
          borderRadius: 'var(--radius)',
          padding: '12px 16px',
          marginBottom: '16px',
          color: 'var(--success)',
          fontSize: '13px',
        }}>
          Extraktion erfolgreich! KI-Konfidenz: {Math.round((result.confidence?.overall ?? 0) * 100)}%
        </div>
        <div style={{ marginBottom: '12px' }}>
          <strong>Investor:</strong> {result.company?.display_name}
        </div>
        {result.profile && (
          <div style={{ marginBottom: '12px' }}>
            <strong>Profil:</strong> {result.profile.profile_name ?? 'Kein Name'}
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
          <button
            className="btn btn-primary"
            onClick={() => window.location.href = `/companies/${result.company?.id}`}
          >
            Investor anzeigen
          </button>
          <button className="btn" onClick={onCancel}>Schließen</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {!companyId && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <button
            className={`btn ${selectedMode === 'new' ? 'btn-primary' : ''}`}
            style={{ flex: 1 }}
            onClick={() => setSelectedMode('new')}
          >
            Neuen Investor anlegen
          </button>
          <button
            className={`btn ${selectedMode === 'add' ? 'btn-primary' : ''}`}
            style={{ flex: 1 }}
            onClick={() => setSelectedMode('add')}
          >
            Zu bestehendem Investor
          </button>
        </div>
      )}

      {selectedMode === 'add' && !companyId && (
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '12px', color: 'var(--text2)', marginBottom: '6px', fontWeight: 500 }}>
            Unternehmen auswählen
          </label>
          <select
            value={selectedCompanyId}
            onChange={e => setSelectedCompanyId(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid var(--border2)',
              borderRadius: 'var(--radius)',
              background: 'var(--surface)',
              fontSize: '13px',
              color: 'var(--text)',
            }}
          >
            <option value="">— Investor wählen —</option>
            {companies.map((c: Company) => (
              <option key={c.id} value={c.id}>{c.display_name}</option>
            ))}
          </select>
        </div>
      )}

      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border2)'}`,
          borderRadius: 'var(--radius-lg)',
          padding: '40px 24px',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragging ? 'var(--accent-light)' : 'var(--surface2)',
          transition: 'all 0.15s',
          marginBottom: '16px',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          style={{ display: 'none' }}
          onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
        />
        <div style={{ fontSize: '32px', marginBottom: '8px' }}>📄</div>
        {file ? (
          <div style={{ fontSize: '13px', color: 'var(--text)' }}>
            <strong>{file.name}</strong>
            <div style={{ color: 'var(--text3)', marginTop: '4px' }}>
              {(file.size / 1024).toFixed(0)} KB
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '13px', color: 'var(--text2)' }}>
            PDF hier ablegen oder klicken zum Auswählen
            <div style={{ color: 'var(--text3)', fontSize: '12px', marginTop: '4px' }}>
              Max. 20 MB
            </div>
          </div>
        )}
      </div>

      {error && (
        <div style={{
          background: 'var(--accent-light)',
          border: '1px solid var(--accent)',
          borderRadius: 'var(--radius)',
          padding: '10px 14px',
          color: 'var(--accent)',
          fontSize: '13px',
          marginBottom: '12px',
        }}>
          {error}
        </div>
      )}

      {loading && (
        <div style={{
          background: 'var(--info-bg)',
          border: '1px solid var(--info)',
          borderRadius: 'var(--radius)',
          padding: '12px 16px',
          color: 'var(--info)',
          fontSize: '13px',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
          KI analysiert PDF...
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onCancel} disabled={loading}>Abbrechen</button>
        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={!file || loading}
        >
          {loading ? 'Analysiere...' : 'KI-Extraktion starten'}
        </button>
      </div>
    </div>
  );
}
